import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import addressApi from "api/landlord/address";
import { Unit } from "types/landlord/address";
import { getErrorMessage } from "utils/error";
import { patchUnitInCaches } from "shared/utils/units-cache";
import {
  SyndicationState,
  apartmentsVariantForKind,
  diffMagicLinkPayload,
  readSyndicationState
} from "./syndication";

export const useUnitSyndication = (unit: Unit) => {
  const queryClient = useQueryClient();
  const defaultVariant = apartmentsVariantForKind(unit.property?.kind ?? null);
  const [syndication, setSyndication] = useState<SyndicationState>(() =>
    readSyndicationState(unit, defaultVariant)
  );
  const committedRef = useRef(syndication);

  const linkIsActive = Boolean(unit.magic_link?.active);
  const unitListed = Boolean(unit.listed);

  const { mutate } = useMutation({
    mutationKey: ["UNIT_SYNDICATION_UPDATE", unit.id],
    mutationFn: async (next: SyndicationState) => {
      const magicLinkId = unit.magic_link?.id;
      if (!magicLinkId) return next;

      const payload = diffMagicLinkPayload(
        { linkIsActive, unitListed, syndication: committedRef.current },
        { linkIsActive, unitListed, syndication: next }
      );
      if (Object.keys(payload).length > 0)
        await addressApi.updateMagicLink(magicLinkId, payload);

      return next;
    },
    onSuccess: (next) => {
      committedRef.current = next;
      patchUnitInCaches(queryClient, unit.id, {
        feed_zillow_enabled: next.zillow,
        feed_zillow_spotlight_enabled: next.zillow && next.zillowSpotlight,
        feed_apartments_com_enabled: next.apartmentsCom,
        feed_apartments_com_variant: next.apartmentsCom
          ? next.apartmentsComVariant
          : null,
        feed_zumper_enabled: next.zumper,
        feed_dwellsy_enabled: next.dwellsy
      });
    },
    onError: (error) => {
      setSyndication(committedRef.current);
      toast.error(getErrorMessage(error));
    }
  });

  const apply = (next: SyndicationState) => {
    setSyndication(next);
    mutate(next);
  };

  return { syndication, defaultVariant, apply };
};
